/**
 * InstallPrompt — calm, dismissable install affordance.
 * ──────────────────────────────────────────────────────
 * Surfaces when the browser has captured a deferred install
 * event (Android / Chromium) OR the user is on iOS Safari and
 * the app isn't already installed. Sits as a quiet bottom-sheet
 * style card on mobile and as a discreet bottom-right card on
 * desktop. NEVER auto-popups in the user's face — there's a
 * 20-second deferral so it doesn't interrupt arrival.
 *
 * Design language:
 *   - Paper-tint background, gold thin border, serif headline.
 *   - Mono uppercase eyebrow ("Install · Optional").
 *   - Two CTAs: Install · Maybe later (records dismissal).
 *   - On iOS: shows A2HS instructions (Share → Add to Home Screen).
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowDownToLine, Share } from "lucide-react";
import usePWA from "../hooks/usePWA";

const VISIBLE_DELAY_MS = 20000;

export default function InstallPrompt() {
  const { canInstall, isStandalone, isIos, hasDismissed, promptInstall, dismissInstall } = usePWA();
  const [readyToShow, setReadyToShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  // Defer visibility so we never interrupt arrival.
  useEffect(() => {
    const t = window.setTimeout(() => setReadyToShow(true), VISIBLE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (isStandalone || hasDismissed || !readyToShow) return null;

  // iOS has no native prompt — show A2HS instructions on tap.
  const showIosCard = isIos && !canInstall;
  const showStandardCard = canInstall;

  if (!showIosCard && !showStandardCard) return null;

  const handleInstall = async () => {
    if (showStandardCard) {
      const { outcome } = await promptInstall();
      if (outcome === "accepted") return;
      // If user dismissed the native dialog, also collapse our card.
      dismissInstall();
    } else if (showIosCard) {
      setIosOpen(true);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="install-prompt"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
        className="fixed z-40 bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-[360px]"
        data-testid="pwa-install-prompt"
      >
        <div className="relative rounded-xl border border-[hsl(var(--accent-gold)/0.45)] bg-[hsl(var(--background))] shadow-floating overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))] to-transparent" />
          <button
            type="button"
            onClick={dismissInstall}
            aria-label="Dismiss install prompt"
            data-testid="pwa-install-dismiss"
            className="absolute right-2 top-2 size-8 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))] transition"
          >
            <X className="size-3.5" strokeWidth={1.5} />
          </button>

          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
              <Sparkles className="size-3" strokeWidth={1.5} />
              <span>Install · Optional</span>
            </div>
            <div className="mt-2 font-serif text-[18px] leading-snug text-[hsl(var(--foreground))]">
              {showIosCard
                ? "Add VÉRAULT to your Home Screen."
                : "Keep VÉRAULT close at hand."}
            </div>
            <div className="mt-1.5 text-[13px] leading-relaxed text-[hsl(var(--foreground-muted))]">
              {showIosCard
                ? "Open your trip, your essentials, and your memories with one tap — even offline."
                : "Install the Member Suite for one-tap access from your home screen, offline-ready and quiet on notifications."}
            </div>

            {iosOpen && showIosCard ? (
              <div className="mt-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background-paper))] p-3.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">How</div>
                <ol className="mt-2 space-y-2 text-[13px] text-[hsl(var(--foreground))]">
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] mt-[2px] text-[hsl(var(--accent-gold))]">01</span>
                    <span>Tap <Share className="inline size-3.5 mx-0.5" strokeWidth={1.5} /> Share in Safari.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] mt-[2px] text-[hsl(var(--accent-gold))]">02</span>
                    <span>Choose <em>Add to Home Screen</em>.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] mt-[2px] text-[hsl(var(--accent-gold))]">03</span>
                    <span>Tap <em>Add</em> in the top-right corner.</span>
                  </li>
                </ol>
              </div>
            ) : null}

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstall}
                data-testid="pwa-install-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] px-4 py-2 text-[12px] font-mono uppercase tracking-[0.18em] hover:bg-[hsl(var(--foreground)/0.88)] transition"
              >
                <ArrowDownToLine className="size-3.5" strokeWidth={1.5} />
                {showIosCard ? "Show me how" : "Install"}
              </button>
              <button
                type="button"
                onClick={dismissInstall}
                data-testid="pwa-install-later"
                className="px-3 py-2 text-[12px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
